// src/pages/.../EditEventModal.jsx
import React, { useState, useEffect } from "react";
import { FaSave, FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { updateEvent } from "../../services/eventServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const TYPE_EVENTS = ["Cours commun Club", "Tournoi Interclubs", "Stage Club"];

const CATE_EVENTS = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
];

const getInitialFormState = () => ({
  titre: "",
  description: "",
  start: "",
  end: "",
  type_event: "",
  level_event: "Club",
  cate_event: [],
  lieu_event: "",
  horaire_event: "",
  photo_url: "",
  agenda_url: "",
  is_active: false,
});

const EditEventModal = ({ isOpen, onClose, fetchEvents, event }) => {
  const [formData, setFormData] = useState(getInitialFormState);
  const [selectedCate, setSelectedCate] = useState("");

  useEffect(() => {
    if (event && isOpen) {
      setFormData({
        titre: event.titre || "",
        description: event.description || "",
        start: event.start || "",
        end: event.end || "",
        type_event: event.type_event || "",
        level_event: event.level_event || "Club",
        cate_event: (event.cate_event || "")
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
        lieu_event: event.lieu_event || "",
        horaire_event: event.horaire_event || "",
        photo_url: event.photo_url || "",
        agenda_url: event.agenda_url || "",
        is_active: !!event.is_active,
      });
      setSelectedCate("");
    }
  }, [event, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQuillHoraireChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      horaire_event: value,
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const handleAddCategory = () => {
    if (selectedCate && !formData.cate_event.includes(selectedCate)) {
      setFormData((prev) => ({
        ...prev,
        cate_event: [...prev.cate_event, selectedCate],
      }));
      setSelectedCate("");
    }
  };

  const handleRemoveCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      cate_event: prev.cate_event.filter((cate) => cate !== category),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!event) {
      toast.error("Événement introuvable");
      return;
    }

    try {
      const eventData = {
        ...formData,
        id: event.id,
        cate_event: formData.cate_event.join(","),
      };

      await updateEvent(eventData);
      fetchEvents();
      toast.success("Événement mis à jour avec succès");
      onClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'événement", error);
      toast.error("Erreur lors de la mise à jour de l'événement");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="relative w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200/60 dark:border-slate-700/60">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-slate-200/70 dark:border-slate-700/70 bg-gradient-to-r from-primary to-primary/80 text-white rounded-t-2xl">
          <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide font-montserrat">
            Modifier l'événement
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-4 space-y-6">
          {/* Infos générales */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Informations générales
            </h4>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  Titre de l'événement
                </label>
                <input
                  type="text"
                  name="titre"
                  placeholder="Titre de l'événement"
                  value={formData.titre}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  Type de l'événement
                </label>
                <select
                  name="type_event"
                  value={formData.type_event}
                  onChange={handleChange}
                  className="select select-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  required
                >
                  <option value="" disabled>
                    Sélectionner un type
                  </option>
                  {TYPE_EVENTS.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Catégories */}
            <div className="grid gap-3 md:grid-cols-[2fr,3fr] items-start">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  Ajouter une catégorie
                </label>
                <div className="flex gap-2">
                  <select
                    name="cate_event"
                    value={selectedCate}
                    onChange={(e) => setSelectedCate(e.target.value)}
                    className="select select-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  >
                    <option value="" disabled>
                      Sélectionner une catégorie
                    </option>
                    {CATE_EVENTS.map((cate) => (
                      <option key={cate} value={cate}>
                        {cate}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="btn btn-primary min-w-[2.5rem] flex items-center justify-center"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-1">
                {formData.cate_event.map((cate, index) => (
                  <div
                    key={`${cate}-${index}`}
                    className="flex items-center gap-1 rounded-full bg-slate-200 dark:bg-slate-800 px-2 py-1 text-xs text-slate-800 dark:text-slate-100"
                  >
                    <span>{cate}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCategory(cate)}
                      className="text-red-500 hover:text-red-400"
                    >
                      <FaTrash className="text-[0.7rem]" />
                    </button>
                  </div>
                ))}
                {formData.cate_event.length === 0 && (
                  <p className="text-[0.7rem] text-slate-500 dark:text-slate-400">
                    Aucune catégorie sélectionnée pour le moment.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Dates & lieu */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Dates & lieu
            </h4>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  Date de début
                </label>
                <input
                  type="date"
                  name="start"
                  value={formData.start}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  Date de fin (optionnelle)
                </label>
                <input
                  type="date"
                  name="end"
                  value={formData.end}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                />
              </div>

              <div className="flex flex-col gap-1 md:col-span-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  Lieu de l'événement
                </label>
                <input
                  type="text"
                  name="lieu_event"
                  placeholder="Lieu de l'événement"
                  value={formData.lieu_event}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                  required
                />
              </div>
            </div>
          </div>

          {/* Description & horaires */}
          <div className="space-y-4 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                Description de l'événement
              </label>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
                <ReactQuill
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  className="quill-editor"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                Horaires de l'événement
              </label>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
                <ReactQuill
                  value={formData.horaire_event}
                  onChange={handleQuillHoraireChange}
                  className="quill-editor"
                />
              </div>
            </div>
          </div>

          {/* Médias & liens */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Médias & liens
            </h4>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  URL de la photo (optionnelle)
                </label>
                <input
                  type="url"
                  name="photo_url"
                  placeholder="https://..."
                  value={formData.photo_url}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                  URL de l'agenda (optionnelle)
                </label>
                <input
                  type="url"
                  name="agenda_url"
                  placeholder="Lien vers un agenda ou une page d’infos"
                  value={formData.agenda_url}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-2 pb-4">
            <Button
              text="Mettre à jour"
              icon={FaSave}
              type="submit"
              className="btn-primary gap-2"
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="btn-ghost gap-2"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventModal;
